// ============================================================================================
// InputManager — API CONTRACT (WP9a → WP9b screens/HUD, boot/main, ClientGame, LocalSession)
// ============================================================================================
// §26 (frozen; unchanged):   attach · poll · sample · device · setGameplayEnabled · setContext ·
//                            bindings.{get,set,reset} · rumble
// WP9a additions (additive; logged in docs/decisions/WP9a.md):
//
//   createInputManager({ bus, settings, onMute, padSource?, initialDevice? }) → InputManagerFull
//     (InputManagerFull = the frozen §26 InputManager + everything below.)
//     Call `attach(canvas, uiRoot)` once. The touch layer is built lazily, as the FIRST child of
//     `uiRoot` (#touch-controls), when touch becomes the device (coarse pointer at boot, or the
//     first touch anywhere): every screen paints above it, so on-screen UI wins its taps.
//
//   Per frame (main loop, §23.2) — main.ts MUST call both:
//     input.poll()            gamepads, device switching, menu navigation, touch visuals, haptics
//     input.syncView(view)    the LIVE view (never a replay view): local slot, state, item and
//                             charges for touch morphing (CLIMB/HOP/THROW/MASH!/PEBBLE, ITEM icon),
//                             haptics, the shove buffer and touch throw aim. It supersedes
//                             setContext(), which still works for code that has no view.
//
//   Bus traffic it produces:
//     'input:device'  'kbm' | 'pad' | 'touch' whenever the most recently used device changes.
//     'input:nav'     menus: up/down/left/right (arrows with UI focus; D-pad, and the left stick
//                     while gameplay is off, with key repeat), confirm (Enter with nothing
//                     focused; pad A with nothing focused or during a replay; a touch tap during
//                     a replay), back (Esc in UI / pad B), tabL/tabR (Q/E or PgUp/PgDn while a
//                     visible [data-tabs] strip exists; pad LB/RB), pause (Esc / Start / touch
//                     PAUSE), scoreboard (Tab / View, on press; hold state is `scoreboardHeld`).
//     Pad A with a focused UI element clicks it (as Enter/Space do natively for the keyboard).
//   Bus traffic it consumes: 'settings' (live: bindings-free settings such as aim assist,
//     hold-to-carry, deadzone, swap A/B, rumble, touch layout/size/stick, UI scale), 'ui:screen'
//     (touch mode), 'replay:start'/'replay:end', 'joined' (local slot fallback), sim events.
//
//   Queries for screens/HUD:
//     input.device / input.padFamily ('xbox'|'playstation'|'switch'|'generic') / input.padConnected
//     input.glyph(action, device?) → Glyph   key cap / pad button / touch name for prompts
//     input.scoreboardHeld                     hold-to-show scoreboard (Tab or View held)
//     input.onChange(fn) → unsubscribe         device, pad family/connection, bindings, touch mode
//     input.touch.mode                         'hidden' | 'stick' | 'full'
//     input.touch.layout                       TouchLayout in CSS px while visible, else null:
//                                              buttons, stickZone, cluster, topCluster and
//                                              `hud` = { topLeftX, bottomCenter: { cx, bottom,
//                                              maxWidth } } — where the mode widget and the Dizzy
//                                              Dial fit without covering a control.
//     The same hints are on the UI root as CSS custom properties (px) while controls show, plus
//     `data-touch-mode`, so HUD CSS can use them with fallbacks, no JS needed:
//       --tc-dial-x --tc-dial-bottom --tc-dial-max-w --tc-top-left-x
//       --tc-cluster-top --tc-cluster-x0 --tc-cluster-x1 --tc-stick-x0 --tc-stick-x1 --tc-stick-top
//       --tc-top-row-x0 --tc-top-row-top --tc-top-row-bottom (HONK/EMOTE and the Practice wheel)
//     input.touch.setMode(mode | 'auto')       override; 'auto' derives from 'ui:screen':
//                                              menu → stick only; game/lobby/podium → full;
//                                              other screens (and gameplay disabled) → hidden.
//     input.touch.setKillFeedHeight(px)        HONK/EMOTE sit right under the kill feed.
//
//   Rebinding (Settings > Controls, §14.4):
//     await input.capture('key' | 'pad', timeoutMs?) → code ('KeyJ', 'Mouse2') | button index |
//       null (cancelled by Esc / timeout / cancelCapture()). Gameplay never sees the captured press.
//     Then: input.bindings.set(rebindKey(input.bindings.get(), action, slot, code).bindings)
//       (`rebindKey` / `rebindPad` / labels and orders live in ./bindings; key and pad labels in
//       ./glyphs). bindings.set/reset persist to localStorage `shoveoff.bindings.v1`, apply at
//       once and fire onChange.
//
//   Practice (§23.7): input.setItemHotkeys(fn | null): number keys 1–6 (unless rebound to a
//     non-emote action) and the touch item wheel call fn(ItemKind). LocalSession sets this itself.
//
//   Touch wheels (EMOTE, Practice items): tap opens the wheel (tap an item, the ✕ hub or the
//     backdrop); press-and-swipe picks by swipe direction (wave up, dance right, laugh down, flex
//     left — the D-pad layout of §8.2).
//
//   Haptics (§8.2, §8.3): automatic from sim events; input.rumble(kind) plays one moment by hand
//     (the "Test rumble" row: RUMBLE has the table).
// ============================================================================================
import { NO_SLOT, TUNE } from '@shared/constants';
import { EV } from '@shared/events';
import { BS, ItemKind, RoomPhase } from '@shared/ids';
import { normalizeV, setV } from '@shared/math';
import { Btn, NEUTRAL_INPUT, type InputFrame, type Vec2 } from '@shared/types';
import { onSim, type Bus } from '../core/bus';
import type { FrameView } from '../game/FrameView';
import type { Settings } from '../profile/settings';
import type { ScreenId } from '../ui/contracts';
import { assistCone, bestAssistDir, touchThrowDir } from './aimAssist';
import {
  cloneBindings, DEFAULT_BINDINGS, keyActionFor, loadBindings, sanitizeBindings, saveBindings, swapAB,
  type Bindings, type KeyAction, type PadAction,
} from './bindings';
import { GamepadInput, PadButton, type PadSource } from './gamepad';
import { glyphFor, padFamilyFromId, type Glyph, type GlyphAction, type InputDevice, type PadFamily } from './glyphs';
import { Haptics, RUMBLE, type RumbleKind } from './haptics';
import { KeyboardMouse, tabsVisible, type NavAction } from './keyboardMouse';
import { TouchControls, type TouchAction, type TouchContext, type TouchMode } from './touch';
import type { TouchLayout } from './touchLayout';

export type { InputDevice, PadFamily, Glyph, GlyphAction } from './glyphs';
export type { RumbleKind } from './haptics';
export type { TouchMode } from './touch';

/** Gameplay context that drives context-morphing buttons and prompts. */
export interface InputContext { carrying: boolean; grabbed: boolean; ghost: boolean; item: ItemKind; charges: number; shoveCdFrac: number; inRound: boolean }

export interface InputManager {
  attach(canvas: HTMLCanvasElement, overlay: HTMLElement): void;
  poll(): void;                                       // once per frame: gamepads, device detection
  sample(tick: number, aim: AimContext): InputFrame;  // builds the frame for one sim tick (counters advance on new presses)
  readonly device: InputDevice;
  setGameplayEnabled(on: boolean): void;              // false while a menu has focus (no gameplay presses generated)
  setContext(c: InputContext): void;
  readonly bindings: { get(): Bindings; set(b: Bindings): void; reset(): void };
  rumble(kind: RumbleKind): void;
}

/**
 * What `createInputManager` returns: the frozen §26 InputManager plus the WP9a additions (kept in
 * a separate interface so code that implements the §26 one, like test fakes, is unaffected).
 */
export interface InputManagerFull extends InputManager {
  readonly padFamily: PadFamily;
  readonly padConnected: boolean;
  readonly scoreboardHeld: boolean;
  syncView(view: FrameView): void;
  glyph(action: GlyphAction, device?: InputDevice): Glyph;
  onChange(fn: () => void): () => void;
  capture(kind: 'key' | 'pad', timeoutMs?: number): Promise<string | number | null>;
  cancelCapture(): void;
  setItemHotkeys(fn: ((kind: ItemKind) => void) | null): void;
  readonly touch: TouchApi;
  detach(): void;
}

export interface TouchApi {
  readonly mode: TouchMode;
  /** Layout in CSS px while visible, else null. */
  readonly layout: TouchLayout | null;
  setMode(mode: TouchMode | 'auto'): void;
  setKillFeedHeight(px: number | undefined): void;
}

export interface AimContext {
  selfX: number; selfY: number; facing: number;
  groundFromScreen(sx: number, sy: number, out: Vec2): boolean;
  targets: { slot: number; x: number; y: number; isTeammate: boolean }[];   // rendered positions
}

export interface InputManagerOptions {
  bus: Bus;
  settings: Settings;
  /** M: toggles mute (owned by the boot layer, which holds the audio engine). */
  onMute(): void;
  /** Gamepad source (tests); defaults to `navigator.getGamepads`. */
  padSource?: PadSource;
  /** Initial device; defaults to 'touch' on coarse-pointer devices (§14.6), else 'kbm'. */
  initialDevice?: InputDevice;
}

const MOUSE_AIM_RECENT_MS = TUNE.input.mouseAimRecentSec * 1000;
const EMOTE_KEYS: readonly KeyAction[] = ['emote1', 'emote2', 'emote3', 'emote4'];
const EMOTE_PADS: readonly PadAction[] = ['emote1', 'emote2', 'emote3', 'emote4'];
const PRESS_ACTIONS = ['shove', 'grab', 'jump', 'item', 'honk'] as const;
type PressAction = (typeof PRESS_ACTIONS)[number];
const HELD_BIT: Readonly<Record<PressAction, number>> = { shove: Btn.SHOVE, grab: Btn.GRAB, jump: Btn.JUMP, item: Btn.ITEM, honk: Btn.HONK };
const COUNTER_4 = 15;
const COUNTER_3 = 7;
const COUNTER_2 = 3;
/**
 * Shove input buffer: a press up to this many ticks before the shove cooldown ends is replayed
 * on the first tick the shove is ready again (re-pressed each tick until the lunge is seen, or
 * for SHOVE_RETRY_TICKS), so mashing BONK never eats a shove.
 */
export const SHOVE_BUFFER_TICKS = 8;
const SHOVE_RETRY_TICKS = 3;
/** Ticks of shove cooldown after the LUNGE tick, as seen by the next sample (§2.5: 48 − 1). */
const SHOVE_CD_SAMPLES = TUNE.shove.cooldownTicks - 1;
const SHOVE_CD_MS = (TUNE.shove.cooldownTicks / 60) * 1000;
const PEBBLE_CD_MS = (TUNE.ghost.pebbleCooldownTicks / 60) * 1000;
/** Menu repeat for held D-pad / stick (ms). */
const NAV_REPEAT_DELAY = 380;
const NAV_REPEAT_RATE = 120;
const NAV_STICK = 0.6;
const ITEM_DIGITS: Readonly<Record<string, ItemKind>> = {
  Digit1: ItemKind.BANANA, Digit2: ItemKind.BOMB, Digit3: ItemKind.GLOVE, Digit4: ItemKind.BALLOON, Digit5: ItemKind.PLUNGER, Digit6: ItemKind.SHAKE,
  Numpad1: ItemKind.BANANA, Numpad2: ItemKind.BOMB, Numpad3: ItemKind.GLOVE, Numpad4: ItemKind.BALLOON, Numpad5: ItemKind.PLUNGER, Numpad6: ItemKind.SHAKE,
};
const PAD_NAMES: Readonly<Record<PadFamily, string>> = { xbox: 'Xbox controller', playstation: 'PlayStation controller', switch: 'Switch Pro Controller', generic: 'Controller' };
const FULL_TOUCH_SCREENS: ReadonlySet<ScreenId> = new Set<ScreenId>(['game', 'lobby', 'podium']);
const IN_ROUND: readonly RoomPhase[] = [RoomPhase.INTRO, RoomPhase.PLAY, RoomPhase.ROUND_END];

function toggleFullscreen(): void {
  const doc = document;
  if (doc.fullscreenElement) void doc.exitFullscreen().catch(() => undefined);
  else void doc.documentElement.requestFullscreen?.().catch(() => undefined);
}

function coarsePointer(): boolean {
  try {
    return typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches;
  } catch {
    return false;
  }
}

function now(): number {
  return performance.now();
}

interface Capture { kind: 'key' | 'pad'; armedAt: number; resolve(v: string | number | null): void; timer: ReturnType<typeof setTimeout> | null }

/** The full InputManager (§8, §26): KB+M, Standard-mapping gamepads and touch, all at once. */
export function createInputManager(opts: InputManagerOptions): InputManagerFull {
  const bus = opts.bus;
  let settings = opts.settings;
  let bindings = loadBindings();
  let enabled = true;
  let device: InputDevice = opts.initialDevice ?? (coarsePointer() ? 'touch' : 'kbm');
  let ctx: InputContext = { carrying: false, grabbed: false, ghost: false, item: ItemKind.NONE, charges: 0, shoveCdFrac: 0, inRound: false };
  let screen: ScreenId | null = null;
  let touchOverride: TouchMode | 'auto' = 'auto';
  let replay = false;
  let itemHotkeys: ((kind: ItemKind) => void) | null = null;
  let capture: Capture | null = null;
  const listeners = new Set<() => void>();

  // Local bean, from syncView (preferred) or learned from local-only events.
  let localSlot: number = NO_SLOT;
  let viewSlotKnown = false;
  const local = { x: 0, y: 0, state: -1 as number, facing: 0, active: false };

  // Press counters (§17.3 InputFrame) and per-device press bookkeeping.
  const counters = { shove: 0, grab: 0, jump: 0, item: 0, honk: 0, emote: 0 };
  let lastEmote = 0;
  const touchPresses: Record<PressAction, number> = { shove: 0, grab: 0, jump: 0, item: 0, honk: 0 };
  const touchReleases: Record<PressAction, number> = { shove: 0, grab: 0, jump: 0, item: 0, honk: 0 };
  let touchEmote = 0;

  // Shove buffer and cooldown estimates (cooldown ring on BONK).
  let shoveCdSamples = 0;
  let bufferedShove = 0;
  let shoveCdStart = -Infinity;
  let pebbleCdStart = -Infinity;

  // Reused per-frame records (no allocation in the hot path).
  const viewCtx: InputContext = { carrying: false, grabbed: false, ghost: false, item: ItemKind.NONE, charges: 0, shoveCdFrac: 0, inRound: false };
  const touchCtx: TouchContext = { ghost: false, carrying: false, grabbed: false, item: ItemKind.NONE, charges: 0, state: -1, cooldown: 0, replay: false, itemWheel: false };
  const pressN: Record<PressAction, number> = { shove: 0, grab: 0, jump: 0, item: 0, honk: 0 };

  const ground: Vec2 = { x: 0, y: 0 };
  const vec: Vec2 = { x: 0, y: 0 };
  const vec2: Vec2 = { x: 0, y: 0 };

  function emitChange(): void {
    for (const fn of listeners) {
      try {
        fn();
      } catch (err) {
        queueMicrotask(() => {
          throw err;
        });
      }
    }
  }

  function setDevice(d: InputDevice): void {
    if (d === device) return;
    device = d;
    if (d === 'touch') buildTouch();
    bus.emit('input:device', d);
    updateTouchMode();
    emitChange();
  }

  // The touch layer is built on first need (a touch device, or the first touch on a touchscreen
  // laptop), so mouse-and-keyboard players never carry its DOM.
  let overlayEl: HTMLElement | null = null;
  let touchBuilt = false;
  const domAbort = new AbortController();
  function buildTouch(): void {
    if (touchBuilt || !overlayEl) return;
    touchBuilt = true;
    touch.attach(overlayEl);
    applyTouchOptions();
  }

  function nav(action: NavAction): void {
    bus.emit('input:nav', { action });
  }

  // ---- Devices --------------------------------------------------------------------------------

  const kbm = new KeyboardMouse({
    bindings: () => bindings,
    gameplayEnabled: () => enabled,
    nav,
    fullscreen: toggleFullscreen,
    mute: () => opts.onMute(),
    activity: () => setDevice('kbm'),
    raw: (code, repeat, mouse) => {
      if (capture && capture.kind === 'key') {
        if (now() < capture.armedAt) return !mouse;
        if (repeat) return true;
        finishCapture(code === 'Escape' ? null : code);
        return true;
      }
      if (capture && capture.kind === 'pad' && !mouse && code === 'Escape') {
        finishCapture(null);
        return true;
      }
      if (!mouse && itemHotkeys && enabled && !kbm.uiHasFocus()) {
        const kind = ITEM_DIGITS[code];
        // Digits rebound to a non-emote action keep that action.
        const bound = kind === undefined ? null : keyActionFor(bindings, code);
        if (kind !== undefined && (bound === null || bound.startsWith('emote'))) {
          if (!repeat) itemHotkeys(kind);
          return true;
        }
      }
      return false;
    },
  });

  const pad = new GamepadInput(opts.padSource);
  const navRepeat = { dir: '' as '' | 'up' | 'down' | 'left' | 'right', next: 0 };

  const touch = new TouchControls({
    press(a: TouchAction) {
      if (!enabled) return;
      touchPresses[a]++;
    },
    release(a: TouchAction) {
      touchReleases[a]++;
    },
    emote(id) {
      if (!enabled) return;
      lastEmote = id;
      touchEmote++;
    },
    pause: () => nav('pause'),
    confirm: () => nav('confirm'),
    item: (kind) => itemHotkeys?.(kind),
    activity: () => setDevice('touch'),
  });

  const haptics = new Haptics({
    bus,
    settings: () => settings,
    device: () => device,
    localSlot: () => localSlot,
    local: () => (local.active ? local : null),
    rumble: (w, s, ms) => pad.rumble(w, s, ms),
    vibrate: (ms) => {
      try {
        navigator.vibrate?.(ms);
      } catch {
        // Not allowed before a user gesture on some browsers.
      }
    },
    now,
  });

  // ---- Bus ------------------------------------------------------------------------------------

  bus.on('settings', (s) => {
    settings = s;
    applyTouchOptions();
  });
  bus.on('ui:screen', (id) => {
    screen = id;
    updateTouchMode();
  });
  bus.on('replay:start', () => {
    replay = true;
  });
  bus.on('replay:end', () => {
    replay = false;
  });
  bus.on('joined', (j) => {
    if (!viewSlotKnown && !j.spectator) localSlot = j.slot;
  });
  onSim(bus, (ev, isLocal, isReplay) => {
    if (isReplay) return;
    // Local-only channel: learn the local slot when no view has told us.
    if (isLocal && !viewSlotKnown) localSlot = ev.a;
    if (ev.a !== localSlot || localSlot === NO_SLOT) return;
    if (ev.type === EV.LUNGE) {
      shoveCdSamples = SHOVE_CD_SAMPLES;
      shoveCdStart = now();
      bufferedShove = 0;
    } else if (ev.type === EV.PEBBLE_DROP) {
      pebbleCdStart = now();
    } else if (ev.type === EV.RESPAWN || ev.type === EV.GHOST_READY) {
      shoveCdSamples = 0;
      shoveCdStart = -Infinity;
    }
  });

  // ---- Touch mode -----------------------------------------------------------------------------

  function derivedTouchMode(): TouchMode {
    if (device !== 'touch') return 'hidden';
    if (touchOverride !== 'auto') return touchOverride;
    if (!enabled || screen === null) return 'hidden';
    if (screen === 'menu') return 'stick';
    return FULL_TOUCH_SCREENS.has(screen) ? 'full' : 'hidden';
  }

  function updateTouchMode(): void {
    const m = derivedTouchMode();
    if (m === touch.mode) return;
    touch.setMode(m);
    emitChange();
  }

  function applyTouchOptions(): void {
    touch.setOptions({
      layout: settings.controls.touch.layout, buttonSize: settings.controls.touch.buttonSize, stick: settings.controls.touch.stick,
      uiScale: settings.accessibility.uiScale / 100, reducedMotion: settings.gameplay.reducedMotion,
    });
  }

  // ---- Gamepad polling: device switch, menu navigation ----------------------------------------

  function padMenuContext(): boolean {
    return !enabled || kbm.uiHasFocus();
  }

  function pollPad(t: number): void {
    const had = pad.connected;
    const active = pad.poll(t);
    if (pad.connectionChanged || had !== pad.connected) emitChange();
    if (had !== pad.connected) {
      // Hot-plug feedback; a lost pad hands the glyphs back to the other devices.
      const name = PAD_NAMES[padFamilyFromId(pad.id)];
      bus.emit('toast', { text: pad.connected ? `${name} connected` : 'Controller disconnected', kind: 'info' });
      if (!pad.connected && device === 'pad') setDevice(coarsePointer() ? 'touch' : 'kbm');
    }
    if (!pad.connected) return;
    if (active) setDevice('pad');

    if (capture && capture.kind === 'pad') {
      for (let b = 0; b < 17; b++) {
        if (pad.takePresses(b) > 0 && t >= capture.armedAt) {
          finishCapture(b);
          pad.clearEdges();
          return;
        }
      }
      pad.clearEdges();
      return;
    }

    // System buttons: Start = pause, View = scoreboard, in every context.
    if (pad.takePresses(PadButton.START) > 0) nav('pause');
    if (pad.takePresses(PadButton.VIEW) > 0) nav('scoreboard');

    if (padMenuContext()) {
      if (pad.takePresses(PadButton.A) > 0) {
        const el = document.activeElement;
        if (el instanceof HTMLElement && kbm.uiHasFocus() && !(el instanceof HTMLInputElement)) el.click();
        else nav('confirm');
      }
      if (pad.takePresses(PadButton.B) > 0) nav('back');
      if (tabsVisible(kbm.overlayRoot) || !enabled) {
        if (pad.takePresses(PadButton.LB) > 0) nav('tabL');
        if (pad.takePresses(PadButton.RB) > 0) nav('tabR');
      }
      // D-pad (and the left stick when gameplay is off) with key repeat.
      let dir: typeof navRepeat.dir = '';
      if (pad.isDown(PadButton.UP)) dir = 'up';
      else if (pad.isDown(PadButton.DOWN)) dir = 'down';
      else if (pad.isDown(PadButton.LEFT)) dir = 'left';
      else if (pad.isDown(PadButton.RIGHT)) dir = 'right';
      if (!dir && !enabled) {
        pad.rawLeft(vec2);
        if (Math.max(Math.abs(vec2.x), Math.abs(vec2.y)) > NAV_STICK) {
          dir = Math.abs(vec2.x) > Math.abs(vec2.y) ? (vec2.x > 0 ? 'right' : 'left') : vec2.y > 0 ? 'down' : 'up';
        }
      }
      for (const b of [PadButton.UP, PadButton.DOWN, PadButton.LEFT, PadButton.RIGHT]) pad.takePresses(b);
      if (dir !== navRepeat.dir) {
        navRepeat.dir = dir;
        navRepeat.next = t + NAV_REPEAT_DELAY;
        if (dir) nav(dir);
      } else if (dir && t >= navRepeat.next) {
        navRepeat.next = t + NAV_REPEAT_RATE;
        nav(dir);
      }
    } else {
      navRepeat.dir = '';
      // A skips replays (§14.2); otherwise A is Jump.
      if (replay && pad.peekPresses(PadButton.A) > 0) nav('confirm');
    }
  }

  // ---- Sampling -------------------------------------------------------------------------------

  function isMenuClaimed(btn: number): boolean {
    return btn === PadButton.A || btn === PadButton.B || btn === PadButton.UP || btn === PadButton.DOWN || btn === PadButton.LEFT
      || btn === PadButton.RIGHT || btn === PadButton.LB || btn === PadButton.RB;
  }

  // Pad helpers run several times per frame: index loops over the live bindings with the Swap
  // Jump/Grab mapping applied inline, so nothing is allocated (`padButtonsFor` is the same rule).
  function physical(raw: number): number {
    return settings.controls.pad.swapJumpGrab ? swapAB(raw) : raw;
  }

  /** Presses of `action` on the pad that gameplay may read now (menus claim A/B/D-pad/LB/RB while focused). */
  function padPresses(action: PadAction, menu: boolean): number {
    const list = bindings.pad[action];
    let n = 0;
    for (let i = 0; i < list.length; i++) {
      const btn = physical(list[i]);
      if (menu && isMenuClaimed(btn)) continue;
      n += pad.takePresses(btn);
    }
    return n;
  }

  function padHeld(action: PadAction, menu: boolean): boolean {
    const list = bindings.pad[action];
    for (let i = 0; i < list.length; i++) {
      const btn = physical(list[i]);
      if (menu && isMenuClaimed(btn)) continue;
      if (pad.isDown(btn)) return true;
    }
    return false;
  }

  function padReleases(action: PadAction): number {
    const list = bindings.pad[action];
    let n = 0;
    for (let i = 0; i < list.length; i++) n += pad.takeReleases(physical(list[i]));
    return n;
  }

  function moveAxes(out: Vec2): Vec2 {
    const kx = (kbm.isHeld('right') ? 1 : 0) - (kbm.isHeld('left') ? 1 : 0);
    // Screen up is sim −y (§2.1).
    const ky = (kbm.isHeld('down') ? 1 : 0) - (kbm.isHeld('up') ? 1 : 0);
    let x = 0;
    let y = 0;
    if (kx !== 0 || ky !== 0) {
      normalizeV(out, kx, ky);
      x = out.x;
      y = out.y;
    }
    if (pad.connected) {
      pad.move(settings.controls.pad.deadzone, vec2);
      x += vec2.x;
      y += vec2.y;
    }
    x += touch.move.x;
    y += touch.move.y;
    const m = Math.hypot(x, y);
    return m > 1 ? setV(out, x / m, y / m) : setV(out, x, y);
  }

  function heldBits(menu: boolean): number {
    let h = 0;
    for (const a of PRESS_ACTIONS) {
      if (kbm.isHeld(a) || (pad.connected && padHeld(a, menu)) || touch.isHeld(a)) h |= HELD_BIT[a];
    }
    return h;
  }

  function mouseAim(aim: AimContext, out: Vec2): Vec2 {
    const m = kbm.mouse;
    if (!settings.gameplay.mouseAim || now() - m.movedAt > MOUSE_AIM_RECENT_MS) return setV(out, 0, 0);
    if (!aim.groundFromScreen(m.x, m.y, ground)) return setV(out, 0, 0);
    return normalizeV(out, ground.x - aim.selfX, ground.y - aim.selfY);
  }

  /** Item/throw aim for the current device (§8.1 mouse, §8.2 right stick, §8.3 touch). */
  function itemAim(aim: AimContext, out: Vec2): Vec2 {
    if (device === 'pad') return pad.aim(now(), out);
    if (device === 'touch') {
      if (touch.swiping) return normalizeV(out, touch.move.x, touch.move.y);
      touchThrowDir(aim.selfX, aim.selfY, aim.facing, aim.targets, out);
      return out;
    }
    return mouseAim(aim, out);
  }

  /** Folds the presses since the last sample into the counters and fills move, held, aim and assist. */
  function fold(frame: InputFrame, aim: AimContext): void {
    const menu = padMenuContext();
    moveAxes(vec);
    frame.mx = vec.x;
    frame.my = vec.y;
    frame.held = heldBits(menu);

    const n = pressN;
    let assistDevice: InputDevice = device;
    for (const a of PRESS_ACTIONS) {
      const k = kbm.takePresses(a);
      const p = pad.connected ? padPresses(a, menu) : 0;
      const t = touchPresses[a];
      touchPresses[a] = 0;
      n[a] = k + p + t;
      if ((a === 'shove' || a === 'grab') && n[a] > 0) assistDevice = t > 0 ? 'touch' : p > 0 ? 'pad' : 'kbm';
    }

    // Hold-to-carry (§8.1): releasing Grab while carrying is the throw press (any device).
    const grabReleased = kbm.takeReleases('grab') + (pad.connected ? padReleases('grab') : 0) + touchReleases.grab > 0;
    for (const a of PRESS_ACTIONS) touchReleases[a] = 0;
    if (settings.gameplay.holdToCarry && ctx.carrying && grabReleased && !kbm.isHeld('grab') && !touch.isHeld('grab')) n.grab++;

    // Shove buffer: a press shortly before the cooldown ends is replayed when it ends.
    const inCooldown = shoveCdSamples > 0;
    const bufferable = !ctx.ghost && !ctx.grabbed && !ctx.carrying;
    if (n.shove > 0 && inCooldown && bufferable && shoveCdSamples <= SHOVE_BUFFER_TICKS) bufferedShove = shoveCdSamples + SHOVE_RETRY_TICKS;
    if (bufferedShove > 0) {
      if (!bufferable) bufferedShove = 0;
      else {
        if (!inCooldown && n.shove === 0) n.shove = 1;
        bufferedShove--;
      }
    }
    if (shoveCdSamples > 0) shoveCdSamples--;

    counters.shove = (counters.shove + Math.min(COUNTER_4, n.shove)) & COUNTER_4;
    counters.grab = (counters.grab + Math.min(COUNTER_4, n.grab)) & COUNTER_4;
    counters.jump = (counters.jump + Math.min(COUNTER_4, n.jump)) & COUNTER_4;
    counters.item = (counters.item + Math.min(COUNTER_4, n.item)) & COUNTER_4;
    counters.honk = (counters.honk + Math.min(COUNTER_3, n.honk)) & COUNTER_3;

    let emotes = 0;
    for (let i = 0; i < EMOTE_KEYS.length; i++) {
      const k = kbm.takePresses(EMOTE_KEYS[i]) + (pad.connected && !menu ? padPresses(EMOTE_PADS[i], false) : 0);
      if (k === 0) continue;
      lastEmote = i + 1;
      emotes++;
    }
    emotes += touchEmote;
    touchEmote = 0;
    if (emotes > 0) counters.emote = (counters.emote + 1) & COUNTER_2;

    itemAim(aim, vec);
    frame.ax = vec.x;
    frame.ay = vec.y;
    if (n.shove === 0 && n.grab === 0) return;
    const cone = assistCone(assistDevice, settings.gameplay.aimAssist, n.shove === 0);
    if (cone && bestAssistDir(aim.selfX, aim.selfY, aim.facing, aim.targets, cone, vec)) {
      frame.sx = vec.x;
      frame.sy = vec.y;
    }
  }

  function clearAllPresses(): void {
    kbm.clearPresses();
    pad.clearEdges();
    for (const a of PRESS_ACTIONS) {
      touchPresses[a] = 0;
      touchReleases[a] = 0;
    }
    touchEmote = 0;
    bufferedShove = 0;
  }

  // ---- Capture (rebinding) --------------------------------------------------------------------

  function finishCapture(v: string | number | null): void {
    const c = capture;
    if (!c) return;
    capture = null;
    if (c.timer !== null) clearTimeout(c.timer);
    kbm.clearPresses();
    c.resolve(v);
  }

  function touchCooldown(t: number): number {
    if (ctx.ghost) return Math.max(0, 1 - (t - pebbleCdStart) / PEBBLE_CD_MS);
    return Math.max(0, 1 - (t - shoveCdStart) / SHOVE_CD_MS);
  }

  const touchApi: TouchApi = {
    get mode() {
      return touch.mode;
    },
    get layout() {
      return touch.layout;
    },
    setMode(m) {
      touchOverride = m;
      updateTouchMode();
    },
    setKillFeedHeight(px) {
      touch.setKillFeedHeight(px);
    },
  };

  applyTouchOptions();

  return {
    get device() {
      return device;
    },
    get padFamily() {
      return pad.family;
    },
    get padConnected() {
      return pad.connected;
    },
    get scoreboardHeld() {
      return kbm.isHeld('scoreboard') || (pad.connected && padHeld('scoreboard', false));
    },
    touch: touchApi,
    attach(canvas, overlay) {
      kbm.attach(canvas, overlay);
      overlayEl = overlay;
      // Any touch anywhere makes touch the current device (glyphs, controls), §8.
      window.addEventListener('pointerdown', (e) => {
        if (e.pointerType === 'touch') setDevice('touch');
      }, { capture: true, passive: true, signal: domAbort.signal });
      if (device === 'touch') {
        buildTouch();
        bus.emit('input:device', 'touch');
      }
      updateTouchMode();
    },
    detach() {
      kbm.detach();
      touch.detach();
      domAbort.abort();
      haptics.dispose();
    },
    poll() {
      const t = now();
      pollPad(t);
      const c = ctx;
      touchCtx.ghost = c.ghost;
      touchCtx.carrying = c.carrying;
      touchCtx.grabbed = c.grabbed;
      touchCtx.item = c.item;
      touchCtx.charges = c.charges;
      touchCtx.state = local.state;
      touchCtx.cooldown = touchCooldown(t);
      touchCtx.replay = replay;
      touchCtx.itemWheel = itemHotkeys !== null;
      touch.setContext(touchCtx);
      touch.frame();
      haptics.update();
    },
    sample(tick, aim) {
      const frame: InputFrame = { tick, ...NEUTRAL_INPUT };
      if (enabled && !capture) fold(frame, aim);
      else {
        clearAllPresses();
        if (shoveCdSamples > 0) shoveCdSamples--;
      }
      frame.cShove = counters.shove;
      frame.cGrab = counters.grab;
      frame.cJump = counters.jump;
      frame.cItem = counters.item;
      frame.cHonk = counters.honk;
      frame.cEmote = counters.emote;
      frame.emote = lastEmote;
      return frame;
    },
    setGameplayEnabled(on) {
      if (on === enabled) return;
      enabled = on;
      clearAllPresses();
      if (!on) touch.releaseAll();
      updateTouchMode();
    },
    setContext(c) {
      ctx = c;
    },
    syncView(view) {
      if (view.source === 'replay') return;
      viewSlotKnown = true;
      localSlot = view.localSlot;
      const me = localSlot !== NO_SLOT && localSlot < view.beans.length ? view.beans[localSlot] : null;
      local.active = me !== null && me.active;
      local.x = me?.x ?? 0;
      local.y = me?.y ?? 0;
      local.facing = me?.facing ?? 0;
      local.state = me && me.active ? me.state : -1;
      viewCtx.carrying = me?.state === BS.CARRYING;
      viewCtx.grabbed = me?.state === BS.GRABBED;
      viewCtx.ghost = me?.ghost ?? false;
      viewCtx.item = me?.item ?? ItemKind.NONE;
      viewCtx.charges = me?.charges ?? 0;
      viewCtx.shoveCdFrac = touchCooldown(now());
      viewCtx.inRound = IN_ROUND.includes(view.roomPhase);
      ctx = viewCtx;
    },
    glyph(action, d = device) {
      return glyphFor(action, d, bindings, pad.family, settings.controls.pad.swapJumpGrab ? swapAB : undefined);
    },
    onChange(fn) {
      listeners.add(fn);
      return () => {
        listeners.delete(fn);
      };
    },
    capture(kind, timeoutMs = 8000) {
      finishCapture(null);
      return new Promise((resolve) => {
        capture = {
          kind, resolve, armedAt: now() + 120,
          timer: timeoutMs > 0 ? setTimeout(() => finishCapture(null), timeoutMs) : null,
        };
        if (kind === 'pad') pad.clearEdges();
      });
    },
    cancelCapture() {
      finishCapture(null);
    },
    setItemHotkeys(fn) {
      const changed = (fn === null) !== (itemHotkeys === null);
      itemHotkeys = fn;
      if (changed) emitChange();
    },
    bindings: {
      get: () => cloneBindings(bindings),
      set(b) {
        bindings = sanitizeBindings(b);
        saveBindings(bindings);
        emitChange();
      },
      reset() {
        bindings = cloneBindings(DEFAULT_BINDINGS);
        saveBindings(bindings);
        emitChange();
      },
    },
    rumble(kind) {
      haptics.play(kind);
    },
  };
}

/** The rumble table (§8.2), for settings previews ("Test rumble"). */
export { RUMBLE };
